import { CardType } from "@/types/enums/payment";
import { BinLookupBrand, BinLookupResult } from "@/types/rainforest";

/**
 * Stripe's answer to Rainforest's BIN lookup.
 *
 * Rainforest emits credit-vs-debit from the card number as the patient types,
 * and that event is what drives dual pricing: the live surcharge line, the
 * card-type badge, and the silent payin-config recreation on a debit/credit
 * flip. Stripe's Elements never hands the card number to us or to the page, so
 * there is nothing to look a BIN up against. The equivalent is
 * `stripe.createConfirmationToken()`, which tokenises what was typed and
 * returns a `payment_method_preview` - brand and funding - *before* anything is
 * authorised.
 *
 * The result is expressed as a `BinLookupResult` on purpose. That is the shape
 * every surcharge consumer already reads - `debitOrCreditFromBinLookup`, the
 * card-type indicator, both checkout providers - and re-typing all of them for
 * a second processor is exactly the work the processor seam exists to avoid.
 */

/** The fields we read off `ConfirmationToken.payment_method_preview.card`. */
export interface StripeCardPreview {
  brand?: string | null;
  funding?: string | null;
  last4?: string | null;
  country?: string | null;
}

/**
 * Stripe's brand strings to the ones the console's card artwork keys on.
 * Anything else is passed through uppercased rather than flattened to OTHER —
 * Stripe really does send `jcb`, `diners` and `unionpay`.
 */
const STRIPE_BRANDS: Record<string, BinLookupBrand> = {
  visa: "VISA",
  mastercard: "MASTERCARD",
  discover: "DISCOVER",
  amex: "AMERICAN_EXPRESS",
};

const STRIPE_FUNDING: Record<string, CardType> = {
  credit: CardType.CREDIT,
  debit: CardType.DEBIT,
  prepaid: CardType.PREPAID,
};

/**
 * A ConfirmationToken's card preview, as a BIN-lookup result.
 *
 * Stripe reports `funding: "unknown"` for some cards, and that carries no more
 * information than a missing value. Both come back as `ERROR`, which is how a
 * failed Rainforest BIN lookup reports too — so consumers behave identically on
 * either processor when the card type cannot be established.
 */
export function binLookupFromStripeCard(
  card: StripeCardPreview | null | undefined,
): BinLookupResult {
  const funding = STRIPE_FUNDING[String(card?.funding ?? "").toLowerCase()];
  if (!card || !funding) {
    return { status: "ERROR", data: null, errors: null };
  }

  const brand = String(card.brand ?? "").toLowerCase();
  return {
    status: "SUCCESS",
    data: {
      brand: STRIPE_BRANDS[brand] ?? brand.toUpperCase(),
      type: funding,
      last_4: card.last4 ?? "",
      country: card.country ?? "",
    },
    errors: null,
  };
}
